import { gql, useApolloClient } from "@apollo/client";
import { Button, Flex } from "@chakra-ui/react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NotFound } from "src/components/Feedback/NotFound";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { useCreateFormTemplate } from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { JsonEditor } from "src/scenes/admin/components/JsonEditor";
import { GET_ENROLLMENT_PERIOD_BY_ID } from "src/scenes/orgAdmin/enrollmentPeriods/graphql/queries";
import formTemplateSchema from "src/schemas/formTemplate.json";
import { evictFormTemplateCache } from "src/services/apolloCacheManager";
import * as breadcrumb from "src/services/breadcrumb";
import * as FormTemplateService from "src/services/formTemplate";
import * as Import from "src/services/formTemplate/import";
import {
  FORM_TEMPLATE_TAB_HASH,
  FormTemplates,
} from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { toTuple } from "src/types/remoteData";
import { EditFormTemplateBackButton } from "./components/BackButton";
import { CREATE_FORM_TEMPLATE } from "./graphql/mutations";
import { GET_QUESTION_TYPES_BY_ORGANIZATION } from "./graphql/queries";

export function New() {
  const organization = useOrganization();
  const { enrollmentPeriodId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const [json, setJson] = React.useState("");

  const [createFormTemplate] = useRemoteDataMutation<
    GQL.CreateFormTemplate,
    GQL.CreateFormTemplateVariables
  >(CREATE_FORM_TEMPLATE);

  const { remoteData } = useRemoteDataQuery<
    GQL.GetEnrollment,
    GQL.GetEnrollmentVariables
  >(GET_ENROLLMENT_PERIOD_BY_ID, {
    variables: { id: enrollmentPeriodId },
    fetchPolicy: "no-cache",
  });

  const { remoteData: questionTypesRemoteData } = useRemoteDataQuery<
    GQL.GetQuestionTypesByOrganization,
    GQL.GetQuestionTypesByOrganizationVariables
  >(GET_QUESTION_TYPES_BY_ORGANIZATION, {
    variables: {
      organizationId: organization.toNullable()?.id ?? "",
    },
    fetchPolicy: "no-cache",
    skip: !organization.hasData(),
  });

  const combinedRemoteData = toTuple(remoteData, questionTypesRemoteData);
  const flags = useFlags(["form-builder-draft-mode"]);
  const { mutateAsync: createFormTemplateRest } = useCreateFormTemplate();
  const apolloClient = useApolloClient();

  const uploadJSONHandler = async (
    customQuestionTypes: GQL.GetQuestionTypesByOrganization_custom_question_type[]
  ) => {
    const formConfig = JSON.parse(json);
    try {
      if (flags["form-builder-draft-mode"].enabled) {
        const response = await createFormTemplateRest({
          body: {
            enrollmentPeriodId,
            name: formConfig.name,
            key: formConfig.key,
            description: formConfig.description,
            schema: formConfig,
          },
        });

        if (!response.data?.data.id) {
          throw new Error("Missing form template id");
        }

        evictFormTemplateCache(apolloClient.cache);

        toast({
          title: "Form created",
          status: "success",
          isClosable: true,
        });
        navigate(
          organization
            .map((org) =>
              FormTemplates.edit(
                org,
                enrollmentPeriodId,
                response.data.data.id,
                FORM_TEMPLATE_TAB_HASH.CONTENT
              )
            )
            .withDefault("#")
        );
      } else {
        const formTemplateId: uuid = crypto.randomUUID();
        const importContextData = await apolloClient.query<
          GQL.GetImportContext,
          GQL.GetImportContextVariables
        >({
          query: gql`
            query GetImportContext($enrollmentPeriodId: uuid!) {
              grade_config(
                where: {
                  grades: { enrollment_period_id: { _eq: $enrollmentPeriodId } }
                }
              ) {
                id
                label
                value
              }

              school(
                where: {
                  grades: { enrollment_period_id: { _eq: $enrollmentPeriodId } }
                }
              ) {
                id
                reference_id
                name
              }
            }
          `,
          variables: { enrollmentPeriodId },
          fetchPolicy: "no-cache",
        });
        if (importContextData.error || !importContextData.data) {
          console.error(importContextData.error);
          throw new Error("Error importing form");
        }

        const schema = Import.generateId(formConfig);
        const importContext = Import.getImportContext(
          schema,
          importContextData.data?.grade_config,
          importContextData.data?.school
        );

        const [importedJSON, errors] = Import.importSchema(
          schema,
          importContext
        );

        if (errors.length > 0) {
          console.error({ errors });
        }

        const variables = FormTemplateService.toVariable(
          importedJSON,
          customQuestionTypes,
          formTemplateId
        );

        await createFormTemplate({
          variables,
          update: evictFormTemplateCache,
          onCompleted: (data) => {
            const formTemplateId = data.insert_form_template_one?.id || "";
            navigate(
              organization
                .map((org) =>
                  FormTemplates.edit(
                    org,
                    enrollmentPeriodId,
                    formTemplateId,
                    FORM_TEMPLATE_TAB_HASH.CONTENT
                  )
                )
                .withDefault("#")
            );
          },
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error creating form",
        status: "error",
        isClosable: true,
      });
    }
  };

  return (
    <GQLRemoteDataView remoteData={combinedRemoteData}>
      {([enrollmentPeriodData, customQuestionTypesData]) => {
        if (enrollmentPeriodData.enrollment_period_by_pk === null) {
          return <NotFound />;
        }

        const { enrollment_period_by_pk: enrollmentPeriod } =
          enrollmentPeriodData;
        return (
          <Flex direction="column" gap={4} height="100%">
            <Breadcrumb
              items={breadcrumb.enrollmentPeriod.getBreadcrumbsForNewFormTemplate(
                organization,
                enrollmentPeriod
              )}
            />
            <JsonEditor
              title="Create form"
              schema={formTemplateSchema}
              text={json}
              onChange={setJson}
              height="100%"
              filename="formTemplate.json"
            />

            <AdminFormButtons sticky>
              <Flex w="100%" justifyContent="space-between">
                <EditFormTemplateBackButton />
                <Button
                  type="button"
                  onClick={() => {
                    uploadJSONHandler(
                      customQuestionTypesData.custom_question_type
                    );
                  }}
                >
                  Create form
                </Button>
              </Flex>
            </AdminFormButtons>
          </Flex>
        );
      }}
    </GQLRemoteDataView>
  );
}
